import React from 'react';
import styled from '@emotion/native';
import { Image } from 'react-native';
import { BodyText, H2 } from '@/shared/components/StyledText';
import {
  green,
  sand,
  welcomeMutedOnGreen,
  white,
} from '@/shared/styles/colors';
import { useWelcomeSectionLayout } from '@/shared/hooks/useWelcomeSectionLayout';
import WelcomeButton from '@/components/welcome/WelcomeButton';
import {
  FONT_STYLES,
  WELCOME_SECTION_BODY_COPY,
  WELCOME_SECTION_HORIZONTAL_BASE,
  WELCOME_SECTION_HORIZONTAL_RHYTHM_EXTRA,
  WELCOME_SECTION_MAX_WIDTH,
} from '@/shared/styles/constants';

import EricaPortrait from '@/assets/images/EricaPortrait.png';

/** Display size; asset can be larger (e.g. @1.5x) for sharp scaling. */
const FOUNDER_PORTRAIT_WIDTH = 540;
const FOUNDER_PORTRAIT_HEIGHT = 582;

interface LayoutProps {
  isWideLayout: boolean;
}

const WelcomeAboutFounderSection = () => {
  const { isWide: isWideLayout } = useWelcomeSectionLayout();

  return (
    <Section isWideLayout={isWideLayout}>
      <SectionInner isWideLayout={isWideLayout}>
        <CopyColumn isWideLayout={isWideLayout}>
          <SectionTitle isWideLayout={isWideLayout}>
            how it started
          </SectionTitle>
          <BodyCopy isWideLayout={isWideLayout}>
            I started EMME because getting dressed shouldn&apos;t feel
            overwhelming — and finding a stylist shouldn&apos;t feel out of
            reach.
          </BodyCopy>
          <BodyCopy isWideLayout={isWideLayout}>
            After years working across fashion, tech, and with stylists
            directly, I saw the same gap everywhere: people needed real, human
            guidance — virtually or in person — and stylists needed a better way
            to run their businesses. EMME is both.
          </BodyCopy>
          <SignatureBlock isWideLayout={isWideLayout}>
            <FounderName isWideLayout={isWideLayout}>Erica Maltz</FounderName>
            <FounderTitle isWideLayout={isWideLayout}>
              Founder & CEO
            </FounderTitle>
          </SignatureBlock>
          <ButtonWrap isWideLayout={isWideLayout}>
            <WelcomeButton
              marginBottom={0}
              alignSelf={isWideLayout ? 'flex-end' : 'center'}
            />
          </ButtonWrap>
        </CopyColumn>
        <PortraitCell>
          <PortraitClip>
            <FounderPortrait
              source={EricaPortrait}
              alt="Erica, founder of EMME"
              accessibilityLabel="Erica, founder of EMME"
            />
          </PortraitClip>
        </PortraitCell>
      </SectionInner>
    </Section>
  );
};

const Section = styled.View<LayoutProps>(({ isWideLayout }) => ({
  width: '100%',
  backgroundColor: green,
  paddingTop: 140,
  paddingBottom: 140,
  paddingHorizontal: isWideLayout
    ? WELCOME_SECTION_HORIZONTAL_BASE
    : WELCOME_SECTION_HORIZONTAL_RHYTHM_EXTRA,
}));

const SectionInner = styled.View<LayoutProps>(({ isWideLayout }) => ({
  width: '100%',
  maxWidth: isWideLayout ? WELCOME_SECTION_MAX_WIDTH : undefined,
  marginLeft: isWideLayout ? 'auto' : 0,
  marginRight: isWideLayout ? 'auto' : 0,
  flexDirection: isWideLayout ? 'row' : 'column-reverse',
  alignItems: isWideLayout ? 'center' : 'stretch',
  justifyContent: isWideLayout ? 'center' : undefined,
  gap: isWideLayout ? 40 : 36,
}));

const CopyColumn = styled.View<LayoutProps>(({ isWideLayout }) => ({
  flexShrink: isWideLayout ? 1 : undefined,
  minWidth: 0,
  maxWidth: isWideLayout ? 520 : undefined,
  alignItems: isWideLayout ? 'flex-end' : 'center',
}));

const SectionTitle = styled(H2)<LayoutProps>(({ isWideLayout }) => ({
  color: sand,
  textAlign: isWideLayout ? 'right' : 'center',
  textTransform: 'uppercase',
  marginBottom: 20,
  letterSpacing: 1,
  width: '100%',
}));

const BodyCopy = styled(BodyText)<LayoutProps>(({ isWideLayout }) => ({
  color: welcomeMutedOnGreen,
  ...FONT_STYLES.welcomeHelveticaNeue,
  fontSize: WELCOME_SECTION_BODY_COPY.fontSize,
  fontWeight: '300',
  letterSpacing: WELCOME_SECTION_BODY_COPY.letterSpacing,
  lineHeight: 24,
  textAlign: isWideLayout ? 'right' : 'center',
  marginBottom: 16,
  width: '100%',
}));

const SignatureBlock = styled.View<LayoutProps>(({ isWideLayout }) => ({
  width: '100%',
  marginBottom: 24,
  alignItems: isWideLayout ? 'flex-end' : 'center',
}));

const FounderName = styled(BodyText)<LayoutProps>(({ isWideLayout }) => ({
  color: welcomeMutedOnGreen,
  ...FONT_STYLES.welcomeHelveticaNeue,
  fontSize: WELCOME_SECTION_BODY_COPY.fontSize,
  fontWeight: '700',
  letterSpacing: WELCOME_SECTION_BODY_COPY.letterSpacing,
  lineHeight: 24,
  textAlign: isWideLayout ? 'right' : 'center',
  marginBottom: 4,
  width: '100%',
}));

const FounderTitle = styled(BodyText)<LayoutProps>(({ isWideLayout }) => ({
  color: welcomeMutedOnGreen,
  ...FONT_STYLES.welcomeHelveticaNeue,
  fontSize: WELCOME_SECTION_BODY_COPY.fontSize,
  fontWeight: '300',
  letterSpacing: WELCOME_SECTION_BODY_COPY.letterSpacing,
  lineHeight: 24,
  textAlign: isWideLayout ? 'right' : 'center',
  marginBottom: 0,
  width: '100%',
}));

const ButtonWrap = styled.View<LayoutProps>(({ isWideLayout }) => ({
  alignSelf: 'stretch',
  alignItems: isWideLayout ? 'flex-end' : 'center',
}));

const PortraitCell = styled.View({
  width: '100%',
  maxWidth: FOUNDER_PORTRAIT_WIDTH,
  alignSelf: 'center',
  minWidth: 0,
  flexShrink: 1,
});

const PortraitClip = styled.View({
  width: '100%',
  maxWidth: FOUNDER_PORTRAIT_WIDTH,
  aspectRatio: FOUNDER_PORTRAIT_WIDTH / FOUNDER_PORTRAIT_HEIGHT,
  borderRadius: 16,
  overflow: 'hidden',
  backgroundColor: white,
  shadowColor: 'transparent',
  shadowOpacity: 0,
  shadowRadius: 0,
  shadowOffset: { width: 0, height: 0 },
  elevation: 0,
});

const FounderPortrait = styled(Image)({
  width: '100%',
  height: '100%',
  resizeMode: 'cover',
  backgroundColor: white,
  shadowColor: 'transparent',
  shadowOpacity: 0,
  shadowRadius: 0,
  shadowOffset: { width: 0, height: 0 },
});

export default WelcomeAboutFounderSection;
